import React, { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useAuth } from "../helpers/AuthContext";
import SummaryApi from "../common";
import {
  FaRegClock,
  FaRegCalendarAlt,
  FaUserTie,
  FaCheckCircle,
  FaGraduationCap, // Used FaGraduationCap for course/certificate for better icon
  FaStar, // Swapped FaRegCheckCircle for FaStar for ratings for clarity
} from "react-icons/fa";
// Assuming you have a logo asset in your project
import logo from "../asset/logo.png";
import dummyInstructor from "../asset/person.png"; // Assuming a default instructor image

const StudentAllSet = () => {
  const navigate = useNavigate();
  const { studentId, authLoaded } = useAuth();

  const [enrollment, setEnrollment] = useState(null);
  const [instructor, setInstructor] = useState(null);
  const [course, setCourse] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  useEffect(() => {
    if (!authLoaded) return;

    const fetchData = async () => {
      // Prioritize studentId from context, fallback to localStorage
      const studentIdFromLocal = studentId || localStorage.getItem("student_id");
      if (!studentIdFromLocal) {
        setError("Student ID not found. Please log in again.");
        setLoading(false);
        return;
      }

      try {
        // 1. Fetch Enrollment Details (Timeslot by Student ID)
        const enrollmentRes = await fetch(SummaryApi.getTimeslotByStudentId.url, {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ student_id: Number(studentIdFromLocal) }),
        });
        const enrollmentData = await enrollmentRes.json();

        if (!enrollmentData.status || !enrollmentData.data?.length) {
          setError("You are not currently enrolled in any course. Please select a course.");
          setLoading(false);
          return;
        }

        const enrolled = enrollmentData.data[0];
        setEnrollment(enrolled);

        // Fetch Instructor and Course details concurrently
        const [instructorRes, courseRes] = await Promise.all([
          fetch(SummaryApi.getActiveInstructors.url),
          fetch(SummaryApi.getActiveCourses.url),
        ]);

        // 2. Process Instructor Data
        const instructorData = await instructorRes.json();
        if (instructorData.status && enrolled.instructor_id) {
          const matchedInstructor = instructorData.data.find(
            (inst) => inst.instructor_id === enrolled.instructor_id
          );
          setInstructor(matchedInstructor || null);
        }

        // 3. Process Course Data
        const courseData = await courseRes.json();
        if (courseData.status && enrolled.course_id) {
          const matchedCourse = courseData.data.find(
            (c) => c.course_id === enrolled.course_id
          );
          setCourse(matchedCourse || null);
        }

      } catch (err) {
        console.error("Error loading data:", err);
        setError("Something went wrong while loading enrollment details.");
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, [authLoaded, studentId, navigate]);


  // Utility function to format date
  const formatDate = (dateStr) =>
    dateStr
      ? new Date(dateStr).toLocaleDateString("en-US", {
          year: "numeric",
          month: "long",
          day: "numeric",
        })
      : "N/A";

  // Function to determine image source
  const getInstructorImage = (inst) => {
    if (inst?.instructor_image) {
      // Note: SummaryApi.Images.url is a guess based on pattern. Use the correct property if different.
      const baseUrl = SummaryApi.instructorImage.url.replace("api", ""); 
      const imagePath = inst.instructor_image.replace("/api", "");
      return `${baseUrl}${imagePath}`;
    }
    return dummyInstructor;
  };

  // --- Render Functions (Loading/Error) ---

  if (loading)
    return (
      <div className="flex justify-center items-center min-h-screen bg-gray-50">
        <p className="text-xl text-[#1C3664] font-medium animate-pulse">Loading your enrollment details...</p>
      </div>
    );

  if (error)
    return (
      <div className="flex flex-col justify-center items-center min-h-screen bg-gray-50 p-4">
        <div className="text-red-600 text-xl text-center p-6 bg-red-100 border border-red-300 rounded-lg shadow-lg">
          <p className="font-semibold mb-4">{error}</p>
          <button
            onClick={() => navigate("/student-panel/course-selection")}
            className="mt-4 bg-blue-600 text-white px-6 py-2 rounded-lg font-medium hover:bg-blue-700 transition duration-300"
          >
            Go to Course Selection
          </button>
        </div>
      </div>
    );

  // --- Main Content Render ---

  return (
    // Main Container (White/Light Grey BG)
    <div className="relative flex flex-col items-center w-full min-h-screen bg-gray-50 font-inter">

      {/* Logo */}
      <div className="w-full flex justify-center mt-6 mb-6">
        <img src={logo} alt="logo" className="w-36 sm:w-44 md:w-52 object-contain" />
      </div>

      {/* Main Wizard Container (Dark Blue Background) */}
  <div
    className="
      relative z-10 bg-[#1C3664] shadow-2xl rounded-2xl
      w-[80%] mt-6 mb-16 px-6 sm:px-8 md:px-10 lg:px-12 py-20 
      overflow-hidden
    "
  >
        {/* Header */}
        <header className="text-center mb-10 text-white">
 <div className="bg-blue-500 rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-4 border-4 border-white shadow-lg">
  <FaCheckCircle className="text-white text-5xl" />
</div>
          <h1 className="text-4xl md:text-5xl font-extrabold text-orange-400 mb-2">
            YOU'RE ALL SET!
          </h1>
          <p className="text-gray-200 text-lg sm:text-xl">Your learning journey begins now</p>
        </header>

        {/* Content Card (White/Light Grey) */}
        <div className="w-full bg-white rounded-xl shadow-2xl p-6 md:p-10 flex flex-col lg:flex-row gap-8">
          
          {/* Left Side: Course Details */}
          <div className="w-full lg:w-1/2 space-y-7 lg:border-r lg:border-gray-200 lg:pr-8">
            <h2 className="text-2xl font-bold flex items-center text-gray-800 border-b pb-2 border-gray-100">
              <FaRegClock className="text-yellow-500 mr-3 text-3xl" />
              Your Learning Plan
            </h2>

            {/* Course Title */}
            <div className="text-lg text-gray-800 bg-blue-50 p-3 rounded-lg border border-blue-100 shadow-sm">
                <p className="font-extrabold mb-1 flex items-center gap-2 text-blue-800">
                    <FaGraduationCap className="text-blue-600" /> Course Name
                </p>
                <p className="font-semibold text-xl">{course?.course_title || "N/A"}</p>
            </div>

            {/* Details Grid */}
            <div className="grid grid-cols-1 sm:grid-cols-2 gap-y-6 gap-x-4">
                
                {/* Category */}
                <div className="flex items-start gap-3 text-gray-700">
                    <span className="p-2 bg-blue-100 rounded-full flex-shrink-0 mt-1"><FaGraduationCap className="text-blue-500 text-lg" /></span>
                    <div>
                    <p className="font-semibold text-base text-gray-800">Category</p>
                    <p className="text-sm">{course?.category_name || "N/A"}</p>
                    </div>
                </div>

                {/* Start Date */}
                <div className="flex items-start gap-3 text-gray-700">
                    <span className="p-2 bg-blue-100 rounded-full flex-shrink-0 mt-1"><FaRegCalendarAlt className="text-blue-500 text-lg" /></span>
                    <div>
                    <p className="font-semibold text-base text-gray-800">Start Date</p>
                    <p className="text-sm">{formatDate(enrollment?.start_date)}</p>
                    </div>
                </div>

                {/* End Date */}
                <div className="flex items-start gap-3 text-gray-700">
                    <span className="p-2 bg-blue-100 rounded-full flex-shrink-0 mt-1"><FaRegCalendarAlt className="text-blue-500 text-lg" /></span>
                    <div>
                    <p className="font-semibold text-base text-gray-800">End Date</p>
                    <p className="text-sm">{formatDate(enrollment?.end_date)}</p>
                    </div>
                </div>

                {/* Instructor Name */}
                <div className="flex items-start gap-3 text-gray-700">
                    <span className="p-2 bg-blue-100 rounded-full flex-shrink-0 mt-1"><FaUserTie className="text-blue-500 text-lg" /></span>
                    <div>
                    <p className="font-semibold text-base text-gray-800">Your Instructor</p>
                    <p className="text-sm">
                        {instructor
                        ? `${instructor.first_name} ${instructor.last_name}`
                        : "N/A"}
                    </p>
                    </div>
                </div>
            </div>
          </div>

          {/* Right Side: Instructor & Schedule */}
          <div className="w-full lg:w-1/2 space-y-7 lg:pl-8">
            <h2 className="text-2xl font-bold flex items-center text-gray-800 border-b pb-2 border-gray-100">
                <FaUserTie className="text-yellow-500 mr-3 text-3xl" />
                Instructor & Schedule
            </h2>

            {/* Instructor Profile Card */}
            <div className="flex items-center gap-6 p-4 bg-gray-50 rounded-xl shadow-inner border border-gray-100">
                <div className="w-20 h-20 rounded-full overflow-hidden shadow-md border-2 border-white flex-shrink-0">
                <img
                    src={getInstructorImage(instructor)}
                    alt="Instructor"
                    className="w-full h-full object-cover"
                />
                </div>
                <div className="space-y-1 text-gray-800 text-sm flex-grow">
                <p className="text-lg font-bold text-gray-900 leading-tight">
                    {instructor ? `${instructor.first_name} ${instructor.last_name}` : "N/A"}
                </p>
                <p className="text-sm">
                    <span className="font-medium text-gray-600">{instructor?.job_title || "N/A"}</span>
                </p>
                <p className="text-xs text-blue-700 italic">
                    {instructor?.total_experience
                    ? `${instructor.total_experience}+ Years Experience`
                    : "N/A"}
                </p>
                <div className="flex items-center text-yellow-500 mt-1">
                    {[...Array(
                    Math.max(0, Math.min(5, parseInt(instructor?.rating) || 0))
                    )].map((_, i) => (
                    <FaStar key={i} className="ml-0.5 text-sm" />
                    ))}
                    <span className="ml-2 text-gray-600 text-xs font-medium">({instructor?.rating || 0}.0)</span>
                </div>
                </div>
            </div>

            {/* Weekly Schedule */}
            <div className="p-4 bg-blue-50 rounded-xl border border-blue-200 shadow-sm">
                <p className="font-bold flex items-center text-gray-800 mb-2 text-lg">
                <FaRegCalendarAlt className="text-blue-600 mr-2" />
                Weekly Schedule
                </p>
                <p className="text-gray-700 whitespace-pre-wrap leading-relaxed font-medium">
                    {enrollment?.time_slot ? enrollment.time_slot.split(', ').map((item, index) => (
                        <div key={index} className="flex justify-between border-b border-blue-100 last:border-b-0 py-1">
                            <span className="text-sm font-semibold">{item.split(': ')[0]}</span>
                            <span className="text-sm text-blue-700">{item.split(': ')[1]}</span>
                        </div>
                    )) : "N/A"}
                </p>
            </div>
            
            {/* Certificate Status */}
            <div className="p-4 bg-green-50 rounded-xl border border-green-200 shadow-sm">
                <p className="font-bold flex items-center text-gray-800 mb-2 text-lg">
                <FaGraduationCap className="text-green-600 mr-2" />
                Certificate
                </p>
                <p className="text-gray-700 font-medium text-base">
                {course?.course_title || "Course"} Completion Certificate Included
                </p>
            </div>
          </div>
        </div>

        {/* Action Buttons */}
        <div className="flex flex-col sm:flex-row justify-center gap-4 mt-12">
          <button
            className="bg-blue-700  text-white px-8 py-3 rounded-lg text-lg font-semibold transition duration-300 shadow-lg transform hover:scale-[1.02]"
            onClick={() => navigate("/")}
          >
            Begin Learning
          </button>
          <button className="bg-white hover:bg-gray-100 border border-gray-300 text-gray-800 px-8 py-3 rounded-lg text-lg font-semibold transition duration-300 shadow-lg">
            Download Schedule
          </button>
        </div>
      </div>
    </div>
  );
};

export default StudentAllSet;